import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, MapPin, MessageCircle, Phone, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { PUBLIC_PHONE_LINK, WHATSAPP_URL } from "@/lib/contact";
import { dictionary, localizePath } from "@/i18n/translations";
import { setDocumentMeta } from "@/lib/seo";

const PHONE_PATTERN = /^[\d\+\-\s]{8,25}$/;

export default function ContactPage({ lang }) {
  const copy = dictionary[lang];

  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "idle", text: "" });
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: `${copy.footer.quickLinks.contact} | ${copy.nav.brand}`,
      description: copy.contactPage.subtitle,
    });
  }, [copy.contactPage.subtitle, copy.footer.quickLinks.contact, copy.nav.brand, lang]);

  const quickLinks = useMemo(
    () => [
      { key: "home", label: copy.footer.quickLinks.home, href: localizePath(lang) },
      { key: "map", label: copy.footer.quickLinks.map, href: `${localizePath(lang)}#map` },
      { key: "properties", label: copy.footer.quickLinks.properties, href: `${localizePath(lang)}#properties` },
      { key: "contact", label: copy.footer.quickLinks.contact, href: localizePath(lang, "/contact") },
      { key: "search", label: copy.footer.quickLinks.advancedSearch, href: localizePath(lang, "/search") },
    ],
    [copy.footer.quickLinks.advancedSearch, copy.footer.quickLinks.contact, copy.footer.quickLinks.home, copy.footer.quickLinks.map, copy.footer.quickLinks.properties, lang],
  );

  const validate = () => {
    if (!form.full_name.trim() || !form.phone.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", text: copy.contactPage.requiredError });
      return false;
    }
    if (!PHONE_PATTERN.test(form.phone.trim())) {
      setStatus({ type: "error", text: copy.contactPage.phoneFormatError });
      return false;
    }
    if (form.message.trim().length < 10) {
      setStatus({ type: "error", text: copy.contactPage.minMessageError });
      return false;
    }
    return true;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: "idle", text: "" });

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/contact/messages", {
        full_name: form.full_name,
        phone: form.phone,
        email: form.email,
        message: form.message,
        lang,
      });

      setForm({ full_name: "", phone: "", email: "", message: "" });
      setStatus({ type: "success", text: copy.contactPage.successMessage });
      toast.success(copy.contactPage.successTitle);
    } catch {
      setStatus({ type: "error", text: copy.common.requestFailed });
      toast.error(copy.contactPage.errorTitle);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-luxury-surface pt-28" data-testid="contact-page-root">
      <main className="mx-auto max-w-7xl space-y-8 px-4 pb-24 sm:px-10" data-testid="contact-page-main">
        <section className="flex flex-wrap items-start justify-between gap-4" data-testid="contact-page-top-section">
          <div className="max-w-3xl space-y-3" data-testid="contact-page-top-content">
            <h1 className="text-4xl font-semibold text-stone-900 sm:text-5xl" data-testid="contact-page-title">
              {copy.contactPage.title}
            </h1>
            <p className="text-sm text-stone-600 sm:text-base" data-testid="contact-page-subtitle">
              {copy.contactPage.subtitle}
            </p>
          </div>

          <Button className="rounded-full bg-gold-600 px-6 text-stone-900 hover:bg-gold-500" data-testid="contact-page-top-cta-button">
            {copy.contactPage.cta}
          </Button>
        </section>

        <section className="grid gap-5 lg:grid-cols-[0.95fr_1.25fr_0.6fr]" data-testid="contact-page-content-grid">
          <Card className="border-gold-500/20 bg-white/90" data-testid="contact-page-info-card">
            <CardHeader>
              <CardTitle data-testid="contact-page-info-title">{copy.contactPage.infoTitle}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-testid="contact-page-info-content">
              <div className="rounded-2xl border border-gold-500/20 bg-amber-50/70 p-4" data-testid="contact-page-working-hours-block">
                <p className="text-xs text-stone-500" data-testid="contact-page-working-hours-title">{copy.contactPage.workingHoursTitle}</p>
                <p className="mt-2 text-sm font-semibold text-stone-800" data-testid="contact-page-working-hours-value">
                  {copy.contactPage.workingHoursValue}
                </p>
              </div>

              <p className="text-sm text-stone-600" data-testid="contact-page-service-message">
                {copy.contactPage.serviceMessage}
              </p>

              <div className="space-y-2" data-testid="contact-page-info-contacts">
                <a href={PUBLIC_PHONE_LINK} className="contact-info-item" data-testid="contact-page-phone-link">
                  <Phone size={15} />
                  <span>+966 50 733 6999</span>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="contact-info-item" data-testid="contact-page-whatsapp-link">
                  <MessageCircle size={15} />
                  <span>WhatsApp</span>
                </a>
                <a href="mailto:info@aamal-aqar.sa" className="contact-info-item" data-testid="contact-page-email-link">
                  <Mail size={15} />
                  <span>info@aamal-aqar.sa</span>
                </a>
                <p className="contact-info-item" data-testid="contact-page-location-text">
                  <MapPin size={15} />
                  <span>{copy.footer.location}</span>
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-gold-500/20 bg-white/90" data-testid="contact-page-form-card">
            <CardHeader>
              <CardTitle data-testid="contact-page-form-title">{copy.contactPage.formTitle}</CardTitle>
            </CardHeader>

            <CardContent>
              <form className="space-y-3" onSubmit={onSubmit} noValidate data-testid="contact-page-form">
                <div className="grid gap-3 sm:grid-cols-2" data-testid="contact-page-form-top-fields">
                  <div className="space-y-1">
                    <label className="text-xs text-stone-600" data-testid="contact-page-full-name-label">{copy.contactPage.fullName}</label>
                    <Input value={form.full_name} onChange={(event) => setForm((prev) => ({ ...prev, full_name: event.target.value }))} data-testid="contact-page-full-name-input" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-stone-600" data-testid="contact-page-phone-label">{copy.contactPage.phone}</label>
                    <Input value={form.phone} onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))} data-testid="contact-page-phone-input" />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-stone-600" data-testid="contact-page-email-label">{copy.contactPage.email}</label>
                  <Input type="email" value={form.email} onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))} data-testid="contact-page-email-input" />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-stone-600" data-testid="contact-page-message-label">{copy.contactPage.message}</label>
                  <Textarea value={form.message} onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))} rows={7} data-testid="contact-page-message-input" />
                </div>

                {status.type === "success" && (
                  <div className="rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="contact-page-success-state">
                    {status.text}
                  </div>
                )}

                {status.type === "error" && (
                  <div className="rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="contact-page-error-state">
                    {status.text}
                  </div>
                )}

                <Button type="submit" className="rounded-full bg-stone-900 px-8 text-cream-50 hover:bg-stone-700" disabled={submitting} data-testid="contact-page-submit-button">
                  {submitting ? copy.contactPage.sending : copy.contactPage.submit}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card className="border-gold-500/20 bg-white/90" data-testid="contact-page-quick-links-card">
            <CardHeader className="space-y-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base" data-testid="contact-page-quick-links-title">
                  {copy.contactPage.quickLinksTitle}
                </CardTitle>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-full border border-gold-500/30 p-2 lg:hidden"
                  onClick={() => setQuickLinksOpen((prev) => !prev)}
                  data-testid="contact-page-quick-links-toggle-button"
                  aria-label={copy.contactPage.collapseQuickLinks}
                >
                  <ChevronDown size={15} className={quickLinksOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                </button>
              </div>
            </CardHeader>
            <CardContent className={`${quickLinksOpen ? "block" : "hidden lg:block"}`} data-testid="contact-page-quick-links-content">
              <div className="space-y-2" data-testid="contact-page-quick-links-list">
                {quickLinks.map((item) => (
                  <Link
                    key={item.key}
                    to={item.href}
                    className={`contact-quick-link ${item.key === "contact" ? "contact-quick-link-active" : ""}`}
                    data-testid={`contact-page-quick-link-${item.key}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
}
